import { UAParser } from "ua-parser-js";
import { getDeviceId } from "../pages/document/util";
import packageJson from "../../package.json";
import viagsApiDev from "../pages/chats/api/axiosClientdev";
import { OneSignalRegisterPayload } from "../types/onesignal";

export const onesignalRegister = async (subscriptionId: string) => {
  const payload: OneSignalRegisterPayload = await buildOneSignalPayload(subscriptionId);

  await viagsApiDev.post("/api/chat/onesignal-register", payload);
};

export const onesignalUnregister = async () => {
  const DeviceUUID = await getDeviceId();

  await viagsApiDev.post("/api/chat/onesignal-unregister", {
    DeviceUUID,
  });
};

export const buildOneSignalPayload = async (subscriptionId: string) => {
  const DeviceUUID = await getDeviceId();

  // Thu thập thông tin máy
  const parser = new UAParser();
  const result = parser.getResult();

  // Xử lý fallback cho các trường có thể bị thiếu
  const browserName = result.browser?.name || "Unknown Browser";
  const browserVersion = result.browser?.version || "Unknown";
  const osName = result.os?.name || "Unknown OS";
  const osVersion = result.os?.version || "Unknown";
  const deviceModel = result.device?.model || "Browser";
  const appLanguage = navigator.language || navigator.languages?.[0] || "en-US";

  // Lấy version từ package.json và build info từ environment
  const appVersion = packageJson.version || "1.0.0";
  // AppBuild: sử dụng NGÀY-THÁNG-NĂM hoặc env variable
  const today = new Date();
  const buildDate = `${String(today.getDate()).padStart(2, "0")}-${String(today.getMonth() + 1).padStart(
    2,
    "0",
  )}-${today.getFullYear()}`;
  const appBuild = process.env.REACT_APP_BUILD_ID || buildDate;

  return {
    DeviceUUID,
    PushToken: subscriptionId,
    DevicePlatform: osName,
    DeviceOS: osVersion,
    DeviceName: `${browserName} ${browserVersion}`.trim(),
    AppLanguage: appLanguage,
    DeviceModel: deviceModel,
    AppBuild: appBuild,
    AppVersion: appVersion,
  };
};

/**
 * Xử lý OneSignal khi user đăng nhập
 */
export const handleOneSignalLogin = async (userId: string) => {
  try {
    // Đợi OneSignal sẵn sàng
    if (!window.OneSignal) {
      console.log("⏳ Đợi OneSignal init...");
      return;
    }

    const previousUserId = localStorage.getItem("previousUserId");
    localStorage.setItem("currentUserId", userId);

    // Nếu user khác với user trước → Logout cũ, login mới
    if (previousUserId && previousUserId !== userId) {
      console.log("🔄 Phát hiện đổi user, chuyển tài khoản OneSignal...");
      await window.OneSignal.logout();
      await window.OneSignal.login(userId);
    }
    // Nếu user giống user trước → Opt-in lại và re-register
    else if (previousUserId === userId) {
      console.log("✅ User cũ đăng nhập lại");
      // Opt-in để subscribe lại
      await window.OneSignal.User.PushSubscription.optIn();

      // Đợi một chút để subscription được tạo
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Lấy subscription ID mới
      const subscriptionId = await window.OneSignal.User.PushSubscription.id;
      if (subscriptionId) {
        console.log("📱 Đăng ký lại với backend:", subscriptionId);
        localStorage.setItem("subscriptionId", subscriptionId);
        await onesignalRegister(subscriptionId);
      } else {
        console.log("⚠️ Chưa có subscription ID, event listener sẽ xử lý");
      }
    }
    // Lần đầu login → Login OneSignal
    else {
      console.log("🆕 Lần đầu đăng nhập");
      await window.OneSignal.login(userId);
    }

    localStorage.setItem("previousUserId", userId);
  } catch (error) {
    console.error("❌ Lỗi OneSignal login:", error);
  }
};

/**
 * Xử lý OneSignal khi user đăng xuất
 */
export const handleOneSignalLogout = async () => {
  try {
    await onesignalUnregister();
    localStorage.removeItem("subscriptionId");
    localStorage.removeItem("currentUserId");

    if (window.OneSignal) {
      // Helper để chạy promise với timeout
      const withTimeout = (promise: Promise<any>, ms: number = 3000) => {
        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`Operation timed out after ${ms}ms`)), ms),
        );
        return Promise.race([promise, timeout]);
      };

      try {
        // Unsubscribe push notifications (kèm timeout)
        await withTimeout(window.OneSignal.User.PushSubscription.optOut()).catch((e) =>
          console.warn("OneSignal optOut timeout/error:", e),
        );
        // Logout user (kèm timeout)
        await withTimeout(window.OneSignal.logout()).catch((e) => console.warn("OneSignal logout timeout/error:", e));
      } catch (e) {
        console.warn("OneSignal cleanup error:", e);
      }
    }
    console.log("✅ Đã logout và unsubscribe OneSignal");
  } catch (error) {
    console.error("❌ Lỗi OneSignal logout:", error);
    throw error;
  }
};
